import React, { useEffect, useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userAPI } from '../services/api';
import { User, Search, Filter, Plus, Shield, XCircle, X, Eye, Edit, Trash2 } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const roleLabels = {
  admin: '관리자',
  manager: '매니저',
  staff: '직원'
};

const statusLabels = {
  active: '활성',
  inactive: '비활성'
};

const Users = () => {
  const { user } = useAuth();
  const { success, error: showError } = useToast();
  const [search, setSearch] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleInput, setRoleInput] = useState('');
  const [statusInput, setStatusInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ idString: '', name: '', password: '', role: 'staff', cemeteryId: '' });
  const modalRef = useRef();
  const addBtnRef = useRef();
  const [modalStyle, setModalStyle] = useState({});

  const { data: users = [], isLoading, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: () => userAPI.getAll(),
    select: (response) => response.data || response,
  });

  // 필터링된 사용자 목록
  const filteredUsers = users.filter(user => {
    if (searchInput && !user.name?.includes(searchInput) && !user.id_string?.includes(searchInput)) {
      return false;
    }
    if (roleInput && user.role !== roleInput) {
      return false;
    }
    if (statusInput && ((statusInput === 'active' && !user.is_active) || (statusInput === 'inactive' && user.is_active))) {
      return false;
    }
    return true;
  });

  useEffect(() => {
    if (!showModal) return;
    if (addBtnRef.current) {
      const rect = addBtnRef.current.getBoundingClientRect();
      setModalStyle({
        position: 'absolute',
        top: rect.bottom + 8 + window.scrollY,
        right: window.innerWidth - rect.right,
        zIndex: 1000,
        minWidth: 340,
        maxWidth: 400
      });
    }
    const handleClick = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target) && !addBtnRef.current.contains(e.target)) {
        setShowModal(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showModal]);

  const openAddModal = () => {
    setEditingUser(null);
    setForm({ idString: '', name: '', password: '', role: 'staff', cemeteryId: '' });
    setShowModal(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setForm({
      idString: user.id_string,
      name: user.name,
      password: '',
      role: user.role,
      cemeteryId: user.cemetery_id || ''
    });
    setShowModal(true);
  };

  // 권한 확인
  if (!hasPermission(user?.role, 'USER_MANAGE')) {
    return (
      <div className="flex-center min-h-screen">
        <div className="text-center">
          <User size={64} className="mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-semibold text-gray-600 mb-2">접근 권한이 없습니다</h2>
          <p className="text-gray-500">사용자 관리 권한이 필요합니다.</p>
        </div>
      </div>
    );
  }

  const handleDelete = async (user) => {
    if (window.confirm('정말로 이 사용자를 삭제하시겠습니까?')) {
      try {
        await userAPI.delete(user.id);
        success('사용자가 성공적으로 삭제되었습니다.');
        refetch();
      } catch (e) {
        showError(e.message || '사용자 삭제에 실패했습니다.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // 백엔드 형식에 맞게 데이터 변환
      const userData = {
        id_string: form.idString,
        name: form.name,
        password: form.password,
        role: form.role,
        cemetery_id: form.cemeteryId || null
      };

      if (editingUser) {
        await userAPI.update(editingUser.id, userData);
        success('사용자가 성공적으로 수정되었습니다.');
      } else {
        await userAPI.create(userData);
        success('사용자가 성공적으로 생성되었습니다.');
      }
      setShowModal(false);
      setEditingUser(null);
      setForm({ idString: '', name: '', password: '', role: 'staff', cemeteryId: '' });
      refetch();
    } catch (e) {
      showError(e.message || (editingUser ? '사용자 수정에 실패했습니다.' : '사용자 생성에 실패했습니다.'));
    }
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setRoleInput('');
    setStatusInput('');
  };

  if (isLoading) {
    return <div className="loading">사용자 정보를 불러오는 중...</div>;
  }

  if (error) {
    return (
      <div className="error">
        사용자 정보를 불러오는 중 오류가 발생했습니다: {error.message}
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">사용자 관리</h1>
        <button 
          ref={addBtnRef}
          className="btn btn-primary flex items-center gap-2" 
          onClick={openAddModal}
        >
          <Plus size={18} /> 사용자 추가
        </button>
      </div>

      {/* 검색 및 필터 */}
      <div className="card mb-6">
        <div className="grid grid-4" style={{ gap: '16px', alignItems: 'end' }}>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              검색
            </label>
            <div className="relative">
              <Search size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="아이디, 이름 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input w-full pl-10"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              권한
            </label>
            <select
              value={roleInput}
              onChange={(e) => setRoleInput(e.target.value)}
              className="select w-full"
            >
              <option value="">전체</option>
              <option value="admin">관리자</option>
              <option value="manager">매니저</option>
              <option value="staff">직원</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700 mb-1">
              상태
            </label>
            <select
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
              className="select w-full"
            >
              <option value="">전체</option>
              <option value="active">활성</option>
              <option value="inactive">비활성</option>
            </select>
          </div>

          <div className="flex gap-2">
            <button type="button" className="btn btn-secondary" onClick={handleClearFilters}>
              초기화
            </button>
          </div>
        </div>
      </div>

      {/* 사용자 목록 테이블 */}
      {filteredUsers.length > 0 ? (
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>아이디</th>
                  <th>이름</th>
                  <th>권한</th>
                  <th>묘지</th>
                  <th>상태</th>
                  <th>최근 로그인</th>
                  <th>관리</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.id}>
                    <td className="font-medium">
                      {user.id_string}
                    </td>
                    <td>
                      {user.name}
                    </td>
                    <td>
                      <span className={`status-badge ${user.role === 'admin' ? 'status-completed' : 
                        user.role === 'manager' ? 'status-progress' : 'status-waiting'}`}>
                        {roleLabels[user.role]}
                      </span>
                    </td>
                    <td>
                      {user.cemetery?.name || user.cemetery_id || '-'}
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'status-completed' : 'status-cancelled'}`}>
                        {user.is_active ? '활성' : '비활성'}
                      </span>
                    </td>
                    <td className="text-sm text-gray-600">
                      {user.last_login_at ? new Date(user.last_login_at).toLocaleString() : '없음'}
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => openEditModal(user)}
                          className="text-blue-600 hover:text-blue-900"
                          title="수정"
                        >
                          <Edit size={16} />
                        </button>
                        {user.is_active && (
                          <button 
                            onClick={() => handleDelete(user)}
                            className="text-red-600 hover:text-red-900"
                            title="비활성화"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="empty-state">
          <User size={48} className="text-gray-300 mb-4" />
          <p className="text-gray-500">사용자가 없습니다.</p>
        </div>
      )}

      {/* 사용자 추가/수정 모달 */}
      {showModal && (
        <>
          {/* 오버레이 */}
          <div
            className="modal-overlay"
            onClick={() => setShowModal(false)}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.3)',
              zIndex: 999,
            }}
          />
          {/* 모달 */}
          <div
            style={{ ...modalStyle, zIndex: 1000, background: 'white', borderRadius: '10px' }}
            className="bg-white rounded-xl shadow-2xl p-6 border border-gray-200 animate-fade-in"
          >
            <div ref={modalRef}>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">{editingUser ? '사용자 수정' : '사용자 추가'}</h2>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    lineHeight: 1,
                    display: 'flex',
                    alignItems: 'center',
                    color: '#9ca3af'
                  }}
                  onMouseOver={e => (e.currentTarget.style.color = '#374151')}
                  onMouseOut={e => (e.currentTarget.style.color = '#9ca3af')}
                  aria-label="닫기"
                >
                  <X size={24} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      아이디
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                      placeholder="아이디를 입력하세요"
                      value={form.idString}
                      onChange={e => setForm(f => ({ ...f, idString: e.target.value }))}
                      required
                      disabled={!!editingUser}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      이름
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="이름을 입력하세요"
                      value={form.name}
                      onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      비밀번호{editingUser && ' (변경 시에만 입력)'}
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="비밀번호를 입력하세요"
                      type="password"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                      required={!editingUser}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      권한
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                      value={form.role}
                      onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                    >
                      <option value="admin">관리자</option>
                      <option value="manager">매니저</option>
                      <option value="staff">직원</option>
                    </select>
                  </div>
                </div>
                
                                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      묘지 ID <span className="text-red-500">*</span>
                    </label>
                    <input
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="묘지 ID를 입력하세요"
                      value={form.cemeteryId}
                      onChange={e => setForm(f => ({ ...f, cemeteryId: e.target.value }))}
                      required
                    />
                  </div>
                
                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                  <button 
                    type="button" 
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    취소
                  </button>
                  <button 
                    type="submit" 
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {editingUser ? '수정' : '추가'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Users; 